import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ImageDensityInteractiveCanvas from './ImageDensityInteractiveCanvas';
import TooltipDetailChart from './TooltipDetailChart';

/**
 * Modal de detalle del heatmap: heatmap arriba (navegable para cambiar de marcador)
 * + subgráfico del tooltip abajo, con la tabla base filtrada por el marcador.
 */
export default function DensityPointModal({
  open,
  onClose,
  selectedPoint,
  onSelectPoint,
  content,
  detail,
  detailRows,
  filterLabel,
  columnDurationFormats,
  columnDateFormats,
  columnDateBuckets,
  chartProps,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const meta = content?.header?.meta || [];
  const markerNo = selectedPoint?.markerNumber;
  const title = [markerNo ? String(markerNo) : '', content?.header?.title].filter(Boolean).join('. ');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '90vh',
          maxHeight: fullScreen ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 1.25,
          pr: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Typography variant="subtitle2" fontWeight={700} noWrap title={title}>
              {title}
            </Typography>
          )}
          {meta.map((text, i) => (
            <Typography key={i} variant="caption" color="text.secondary" display="block">
              {text}
            </Typography>
          ))}
        </Box>
        <IconButton aria-label="Cerrar detalle" onClick={onClose} edge="end" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box
          sx={{
            flex: '0 0 auto',
            height: fullScreen ? '42vh' : '36vh',
            minHeight: 180,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ImageDensityInteractiveCanvas
              {...chartProps}
              selectedPoint={selectedPoint}
              onSelectPoint={onSelectPoint}
              interactive
              hitRadiusMultiplier={1.75}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.75, flexShrink: 0 }}>
            Toca otro punto del mapa para cambiar el detalle
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1.5, py: 1.5 }}>
          {filterLabel ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
              <Chip size="small" color="primary" variant="outlined" label={filterLabel} />
              <Typography variant="caption" color="text.secondary">
                {detailRows?.length ?? 0} fila{detailRows?.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          ) : null}
          {/* key: reinicia zoom y filtros de tabla al cambiar de marcador */}
          <TooltipDetailChart
            key={filterLabel || 'detail'}
            detail={detail}
            rows={detailRows}
            columnDurationFormats={columnDurationFormats}
            columnDateFormats={columnDateFormats}
            columnDateBuckets={columnDateBuckets}
            minHeight={fullScreen ? 260 : 300}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
